import { jwtDecode } from 'jwt-decode';
import api from '../axios.config';
import { API_ENDPOINTS, createEndpoint } from './endpoints';

/**
 * Interview invite — candidate-facing, token-authenticated.
 *
 * No bearer auth: the signed link `token` (from the page URL) is passed as a
 * `?token=` query param. The backend path needs `sec_mapping_id`, which is
 * carried inside the token's JWT payload (`secmappingid`), so we decode it
 * client-side only to route the request — the backend re-verifies the token.
 * `api` unwraps to the response body, and the payload sits under `data`.
 */
const unwrap = (response) => response?.data?.data ?? response?.data ?? null;

const secMappingIdFromToken = (token) => {
  try {
    const { secmappingid } = jwtDecode(token) || {};
    return secmappingid ?? null;
  } catch {
    return null;
  }
};

export const InterviewInviteService = {
  // GET the interview invite info for the current status.
  getInfo: async (token) => {
    const secMappingId = secMappingIdFromToken(token);
    if (secMappingId == null) {
      throw new Error('Invalid interview link');
    }
    const endpoint = createEndpoint(API_ENDPOINTS.INTERVIEW_INVITE.INFO, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.get(
      `${endpoint}?token=${encodeURIComponent(token)}`
    );
    return unwrap(response);
  },

  // POST the candidate's attendance decision — { is_accepted: bool }.
  acknowledge: async (token, isAccepted) => {
    const response = await api.post(
      `${API_ENDPOINTS.INTERVIEW_INVITE.ACKNOWLEDGE}?token=${encodeURIComponent(
        token
      )}`,
      { is_accepted: isAccepted }
    );
    return unwrap(response);
  },

  // ── Protected mirrors (bearer auth, no token — scoped by sec_mapping_id).
  // Same payload shape as the magic-link endpoints above; used when this
  // flow is opened from the authenticated "My Applications" list instead of
  // an email link.
  getInfoProtected: async (secMappingId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.MY_PIPELINES.INTERVIEW_INVITE,
      { sec_mapping_id: secMappingId }
    );
    const response = await api.get(endpoint);
    return unwrap(response);
  },

  acknowledgeProtected: async (secMappingId, isAccepted) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.MY_PIPELINES.ACKNOWLEDGE_INTERVIEW,
      { sec_mapping_id: secMappingId }
    );
    const response = await api.post(endpoint, { is_accepted: isAccepted });
    return unwrap(response);
  },
};

export default InterviewInviteService;
